import {
  FocusEvent,
  MouseEvent,
  forwardRef,
  useCallback,
  useState,
  useMemo,
  useId,
} from "react";
import { IconPressEvent, InputRef } from "../input/types";
import { useUpdateEffect, useLatestRef } from "../common/hooks";
import { Options } from "./components/options";
import { Container } from "./components/container";
import { Search } from "./components/search";
import { NOT_FOUND_LABEL_TEXT } from "./constants";
import { OptionsType, OptionType, SelectProps } from "./types";
import { useExpandedState, useSelectMode, useOptionsState } from "./hooks";
import {
  defaultOptionSelectionComparator,
  filterOptions as defaultFilterOptions,
  formatSelectInputValue,
  getCheckedAdditionalLabel,
} from "./utils";
import { useSearchValue } from "./hooks/use-search-value";
import { SelectContextProvider } from "./context";

export const Select = forwardRef<InputRef, SelectProps>(
  (
    {
      id: idFromProps,
      hint,
      error,
      label,
      className,
      listBoxWidth,
      listBoxAlignment,
      type = "single",
      disabled = false,
      clearable = true,
      searchable = false,
      dataTestId = "select",
      notFoundLabel = NOT_FOUND_LABEL_TEXT,
      searchValue: searchValueFromProps,
      options: optionsFromProps = [],
      expanded: isExpanded = false,
      optionsFooter,
      onBlur,
      onFocus,
      onClear,
      onClick,
      onChange,
      onSearch,
      onAfterClose,
      onAfterOpen,
      filterOptions = defaultFilterOptions,
      optionSelectionComparator = defaultOptionSelectionComparator,
      ...restProps
    },
    forwardedRef
  ) => {
    const generatedId = useId();
    const id = idFromProps || generatedId;

    const { isSingleMode, isMultipleMode, isSearchableMode } = useSelectMode(
      type,
      searchable
    );

    const [searchFocused, setSearchFocused] = useState(false);
    const [isSearchFieldTouched, setIsSearchFieldTouched] = useState(false);

    const { expanded, setExpanded } = useExpandedState(isExpanded);

    const getSelectedLabel = useCallback(
      (options: OptionsType) =>
        formatSelectInputValue({
          options,
          searchValue: searchValueFromProps,
          searchable: isSearchableMode,
          type,
          expanded,
        }),
      [searchValueFromProps, isSearchableMode, type, expanded]
    );

    useUpdateEffect(() => {
      if (expanded) {
        onAfterOpen?.();
      } else {
        if (isSearchFieldTouched) {
          setIsSearchFieldTouched(false);
        }

        onAfterClose?.();
      }
    }, [expanded]);

    const { options, clearOptions, updateCheckedOptions } = useOptionsState({
      options: optionsFromProps,
      type,
      onChange,
    });

    const {
      handleClearSearchValue: clearSearchValue,
      searchValue,
      setSearchValue,
    } = useSearchValue({
      labelFromOptions: getSelectedLabel(options),
      searchable: isSearchableMode,
      isMultipleMode,
      searchValue: searchValueFromProps,
      isFocused: searchFocused,
      expanded,
      onSearch,
    });

    const filterOptionsRef = useLatestRef(filterOptions);
    const filteredOptions = useMemo(
      () =>
        !isSearchFieldTouched
          ? options
          : filterOptionsRef.current(options, searchValue),
      [filterOptionsRef, isSearchFieldTouched, options, searchValue]
    );

    const handleBlur = (event: FocusEvent<HTMLInputElement>) => {
      setSearchFocused(false);
      setExpanded(false);
      onBlur?.(event);
    };

    const handleClick = (
      event: MouseEvent<HTMLInputElement> | IconPressEvent
    ) => {
      setExpanded((expanded) => !expanded);
      onClick?.(event);
    };

    const handleClearOptions = (event?: MouseEvent<HTMLButtonElement>) => {
      clearSearchValue();
      clearOptions();
      setExpanded(isSearchableMode);
      onClear?.(event);
    };

    const handleOptionClick = (option: OptionType) => {
      const { label, checked, disabled } = option;

      if (disabled) {
        return;
      }

      if (isSingleMode) {
        setExpanded(false);

        if (checked && !isSearchableMode) {
          return;
        }
      }

      if (!(isMultipleMode && isSearchableMode)) {
        setSearchValue(label);
        setSearchFocused(false);
      }

      updateCheckedOptions(option, optionSelectionComparator);
    };

    const handleInputChange = (value: string) => {
      setSearchValue(value, true);
      setExpanded(true);

      if (!isSearchFieldTouched) {
        setIsSearchFieldTouched(true);
      }
    };

    const handleInputFocus = (event: FocusEvent<HTMLInputElement>) => {
      setSearchFocused(true);
      onFocus?.(event);
    };

    const hasNotFoundLabel =
      typeof notFoundLabel === "string" && notFoundLabel.length > 0;

    const isOptionsExpanded =
      !disabled && expanded && (hasNotFoundLabel || filteredOptions.length > 0);

    const search = (
      <Search
        id={`${id}-input`}
        hint={hint}
        error={error}
        label={label}
        expanded={isOptionsExpanded}
        searchable={isSearchableMode}
        additionalLabel={getCheckedAdditionalLabel(options)}
        value={searchValue}
        ref={forwardedRef}
        disabled={disabled}
        type={type}
        options={options}
        clearable={clearable}
        dataTestId={`${dataTestId}-input`}
        onFocus={handleInputFocus}
        onChange={handleInputChange}
        onClear={handleClearOptions}
        onClick={handleClick}
        {...restProps}
      />
    );

    return (
      <SelectContextProvider id={id}>
        <Container
          disabled={disabled}
          searchable={isSearchableMode}
          expanded={isOptionsExpanded}
          className={className}
          listBoxWidth={listBoxWidth}
          listBoxAlignment={listBoxAlignment}
          dataTestId={dataTestId}
          onBlur={handleBlur}
          onFocus={onFocus}
          searchComponent={search}
        >
          <Options
            type={type}
            options={filteredOptions}
            footer={optionsFooter}
            searchable={isSearchableMode}
            searchValue={!isSearchFieldTouched ? "" : searchValue.toLowerCase()} // Данное условие необходимо для отмены подсветки при открытии списка опций по клику
            notFoundLabel={notFoundLabel}
            dataTestId={`${dataTestId}-options`}
            onClick={handleOptionClick}
          />
        </Container>
      </SelectContextProvider>
    );
  }
);
